import React from 'react';
import { cn } from '@/lib/utils';
import { Tooltip } from 'antd';
import type { KeywordWithMetrics } from './KeywordPanel';

/**
 * Format large numbers with K/M suffix
 */
function formatNumber(num: number | undefined): string {
  if (num === undefined || num === null) return '-';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

/**
 * Get difficulty label based on score
 */
function getDifficultyLabel(kd: number | undefined): string {
  if (kd === undefined) return 'Unknown';
  if (kd <= 20) return 'Very Easy';
  if (kd <= 40) return 'Easy';
  if (kd <= 60) return 'Medium';
  if (kd <= 80) return 'Hard';
  return 'Very Hard';
}

interface KeywordMetricsBadgeProps {
  keyword: KeywordWithMetrics;
  isPrimary?: boolean;
}

/**
 * Compact badge showing a keyword with its metrics
 * Format: keyword | SV: value↗ • KD: value↗ • CPC: $value↗
 */
const KeywordMetricsBadge: React.FC<KeywordMetricsBadgeProps> = ({ keyword, isPrimary }) => {
  const hasMetrics = keyword.search_volume !== undefined ||
                     keyword.keyword_difficulty !== undefined ||
                     keyword.cpc !== undefined;

  // Build metrics array for display with bullet separators
  const metrics: string[] = [];
  if (keyword.search_volume !== undefined) {
    metrics.push(`SV: ${formatNumber(keyword.search_volume)}↗`);
  }
  if (keyword.keyword_difficulty !== undefined) {
    metrics.push(`KD: ${keyword.keyword_difficulty}↗`);
  }
  if (keyword.cpc !== undefined && keyword.cpc > 0) {
    metrics.push(`CPC: $${Math.round(keyword.cpc)}↗`);
  }

  return (
    <Tooltip
      title={
        <div className="text-xs space-y-1">
          <div className="font-medium">{keyword.keyword}</div>
          {keyword.search_volume !== undefined && (
            <div>Monthly Search Volume: {keyword.search_volume.toLocaleString()}</div>
          )}
          {keyword.keyword_difficulty !== undefined && (
            <div>Keyword Difficulty: {keyword.keyword_difficulty} ({getDifficultyLabel(keyword.keyword_difficulty)})</div>
          )}
          {keyword.cpc !== undefined && keyword.cpc > 0 && (
            <div>Cost Per Click: ${keyword.cpc.toFixed(2)}</div>
          )}
        </div>
      }
    >
      <div
        className={cn(
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border bg-card',
          isPrimary
            ? 'border-blue-300 dark:border-blue-700 shadow-sm'
            : 'border-border'
        )}
      >
        <span className="font-medium truncate max-w-[180px]">{keyword.keyword}</span>

        {hasMetrics && (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {metrics.join(' • ')}
          </span>
        )}
      </div>
    </Tooltip>
  );
};

interface KeywordMetricsHeaderProps {
  primaryKeyword: KeywordWithMetrics;
  secondaryKeywords?: KeywordWithMetrics[];
  className?: string;
  /** Compact mode shows only primary keyword inline */
  compact?: boolean;
}

/**
 * Header component displaying the target keyword(s) with their SEO metrics.
 * Shows MSV (Monthly Search Volume), KD (Keyword Difficulty), and CPC.
 *
 * Use this at the top of blog editor pages to give context about the
 * keywords being targeted.
 */
export const KeywordMetricsHeader: React.FC<KeywordMetricsHeaderProps> = ({
  primaryKeyword,
  secondaryKeywords = [],
  className,
  compact = false,
}) => {
  const hasSecondaryKeywords = secondaryKeywords.length > 0;

  // Compact mode: inline display
  if (compact) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <KeywordMetricsBadge keyword={primaryKeyword} isPrimary />
        {hasSecondaryKeywords && secondaryKeywords.length <= 2 && secondaryKeywords.map((kw, idx) => (
          <KeywordMetricsBadge key={idx} keyword={kw} />
        ))}
        {hasSecondaryKeywords && secondaryKeywords.length > 2 && (
          <Tooltip
            title={
              <div className="text-xs">
                <div className="font-medium mb-1">Secondary Keywords:</div>
                {secondaryKeywords.map((kw, idx) => (
                  <div key={idx}>{kw.keyword}</div>
                ))}
              </div>
            }
          >
            <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded-full cursor-help">
              +{secondaryKeywords.length} more
            </span>
          </Tooltip>
        )}
      </div>
    );
  }

  // Full mode: horizontal scrollable keyword row
  // Combine primary + secondary keywords, dedup by lowercase keyword
  const seen = new Set<string>([primaryKeyword.keyword.toLowerCase()]);
  const dedupedSecondary = secondaryKeywords.filter(kw => {
    const key = kw.keyword.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const allKeywords = [
    { ...primaryKeyword, is_primary: true },
    ...dedupedSecondary.map(kw => ({ ...kw, is_primary: false }))
  ];

  return (
    <div className={cn('border-b border-border bg-card overflow-hidden', className)}>
      {/* Horizontal scrollable keyword row */}
      <div className="flex items-center gap-2 px-2 py-2 overflow-x-auto max-w-full">
        {/* Add keyword button placeholder */}
        <button
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-dashed border-border rounded-md text-muted-foreground hover:border-border hover:text-muted-foreground"
          title="Add keyword"
        >
          +
        </button>

        {/* Keyword badges */}
        {allKeywords.map((kw, idx) => (
          <KeywordMetricsBadge key={idx} keyword={kw} isPrimary={kw.is_primary} />
        ))}
      </div>
    </div>
  );
};

export default KeywordMetricsHeader;
